export interface Store {
  storeId: number;
  chainId: number;
  storeName: string;
  storeCode: string | null;
  legibleName: string;
  timeZone: string;
  openTime: string;
  closeTime: string;
  chainName: string;
  chainLegibleName: string;
  isActive: boolean;
}

export type Rect = [number, number, number, number]; // x, y, width, height

export interface BBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  productId: number;
  itemNumber: string;
  upc: string;
  name: string | null;
  imageUri: string | null;
  productImageUri?: string | null;
  hasFallbackImage?: boolean;
  modCoverageId: number;
  templateId?: number;
  shelf?: number;
  column?: number;
  shelfColumnLabel?: string; // Format: "mod-shelf-column" e.g., "1-2-3"
  [key: string]: any; // Allow additional properties
}

export interface TemplateBBox extends BBox {
  tagName: string;
  bboxId: number;
}

export interface PogBBox extends BBox {
  embeddingCropCount: number | null;
  ilItemId: number;
  ilSectionId: number;
  pogImageUri: string;
  shelf?: number;
  column?: number;
  isUnstructured?: boolean;
  isActive: boolean;
  // Remote DC: the fallback image is the Design's representative (starred) Crop, not a product image.
  isRepresentativeCropImage?: boolean | null;
}

export interface BoxAction {
  bboxId: number;
  tagName: string;
  productId?: number;
  upc: string;
  oosDetection?: OosDetectionState;
  occupancy?: number;
  produceIssue?: 'faceOver' | 'presentability';
  commodityFound?: string[];
  expectedCommodity?: string;
}

export enum OosDetectionState {
  IN = 'in',
  LOW = 'low',
  OUT = 'out',
  NA = 'n/a',
  ZIN = 'zin',
}

export enum RogItemStatus {
  CORRECT = 'correct',
  MOVE = 'move',
  OUT = 'out',
  REMOVE = 'remove',
  NA = 'na',
  UNKNOWN = 'unknown',
  SWAP = 'swap',
}

export enum ComplianceStatus {
  COMPLIANT = 'compliant',
  NON_COMPLIANT = 'non_compliant',
  OUT = 'out',
  FACEOVER = 'faceover',
  UNKNOWN = 'unknown',
}

export enum ImageQuality {
  UNKNOWN = 'unknown',
  OK = 'ok',
  SHIFTED = 'shifted',
  INCONSISTENT = 'inconsistent',
  OBSTRUCTED = 'obstructed',
}

export enum CoffeeMachineState {
  UNKNOWN = 'unknown',
  ON = 'on',
  OFF = 'off',
  ERROR = 'error',
  OBSTRUCTED = 'obstructed',
}

export enum MessinessState {
  UNKNOWN = 'unknown',
  MESSY = 'messy',
  CLEAN = 'clean',
  OBSTRUCTED = 'obstructed',
}

export enum CupsState {
  UNKNOWN = 'unknown',
  MISSING = 'missing',
  PRESENT = 'present',
  OBSTRUCTED = 'obstructed',
}

export enum LidsState {
  UNKNOWN = 'unknown',
  MISSING = 'missing',
  PRESENT = 'present',
  OBSTRUCTED = 'obstructed',
}

export enum ProduceVisionIssues {
  SPOILED = 'spoiled',
  UNFRESH = 'unfresh',
  OUT = 'out',
  LOW = 'low',
  OVERFLOW = 'overflow',
  MIXED = 'mixed',
  WRONG_BOX = 'wrong_box',
  OCCLUSION = 'occlusion',
  IMAGE_DEFECT = 'image_defect',
  POG_NONCOMPLIANT = 'pog_noncompliant',
  MOD_OUT_OF_SCOPE = 'mod_out_of_scope',
  BOX_UNPAIRED = 'box_unpaired',
}

export enum OcrConfidence {
  SKIPPED = '0',
  LOW = '1',
  MEDIUM = '2',
  HIGH = '3',
}

export const ISSUE_REASONS = [
  { value: 'camera_angle_incorrect', label: 'Camera angle is incorrect' },
  { value: 'camera_shifted', label: 'Camera has shifted' },
  { value: 'permanent_obstruction', label: 'Permanent obstruction' },
  { value: 'image_overexposed', label: 'Image overexposed' },
  { value: 'image_underexposed', label: 'Image underexposed' },
  { value: 'image_blurry', label: 'Image is blurry' },
  { value: 'shelf_distance_incorrect', label: 'Shelf too close or too far' },
  { value: 'resolution_poor', label: 'Resolution is poor' },
  { value: 'planogram_mismatch', label: 'Planogram does not match shelf' },
  { value: 'planogram_unstructured', label: 'Planogram is unstructured' },
  { value: 'planogram_missing', label: 'Planogram is missing' },
  { value: 'ssm_prediction_issue', label: 'SSM prediction issue' },
  { value: 'pog_compliance_unavailable', label: "Can't run POG Compliance" },
] as const;

export type IssueReasonValue = (typeof ISSUE_REASONS)[number]['value'];

export type ReportIssueSource =
  | 'ai_audit'
  | 'control_store'
  | 'oos_correction'
  | 'forge_remote_dc'
  | 'data_collection';

export interface ReportIssuePayload {
  image_id: number;
  /** Records the report on this Forge task's review. */
  task_id?: string;
  image_url: string;
  mod_tag: string | null;
  reason: IssueReasonValue;
  note?: string;
  source: ReportIssueSource;
}

export type ReportIssueTaskTarget = 'field_tool' | 'action_tool';

/** What the report's follow-up task attempt amounted to on the backend. */
export interface ReportIssueTaskOutcome {
  target: ReportIssueTaskTarget | null;
  taskType: string | null;
  created: boolean;
  skipReason: string | null;
}

export interface ReportedIssueResponse {
  id: string;
  taskOutcome?: ReportIssueTaskOutcome | null;
}

export interface CameraRecentReport {
  id: string;
  reason: string;
  source: string;
  modTag: string | null;
  reportedByEmail: string | null;
  createdAt: string;
}

/** One open field-tool (store ops) task on a camera. */
export interface CameraFieldTask {
  taskType: string;
  source: string | null;
  description?: string | null;
  createdAt: string | null;
}

/** One open action-tool task (obstruction / PSI / POG reset) on a camera's mods. */
export interface CameraActionTask {
  taskType: string;
  taskId: number | null;
  modCoverageId: number | null;
  createdAt: string | null;
}

export interface CameraRecentReportsResponse {
  items: CameraRecentReport[];
  fieldTasks: CameraFieldTask[];
}

export type OosDetectionParams = {
  detections: {
    product_id?: number;
    upc?: string;
    state: OosDetectionState;
  }[];
  image_id?: number;
};

export type OosDetectionResponse = {
  label_items: {
    product_id?: number;
    upc?: string;
    state: OosDetectionState;
    oos_id?: number;
    task_id?: number;
    task_type?: string;
    task_state?: string;
  }[];
  device_id: number;
  device_name: string;
  camera_id: number;
  status_id: number;
  image_id: number;
  record_id: number;
  model_results: Record<string, any>;
};

export type FlexSpaceDetectionParams = {
  image_id?: number;
  template_id?: number;
  occupancies?: number[];
};

export type CoffeeDetectionParams = {
  image_id: number;
  image_quality: ImageQuality;
  coffee_machine_state: CoffeeMachineState;
  messiness_state: MessinessState;
  cups_state: CupsState;
  lids_state: LidsState;
};

export type ProduceVisionParams = {
  image_id: number;
  boxes_info: {
    bbox_id: number;
    pog_name?: string;
    expected_commodity?: string;
    commodity_found: string[];
    issues: ProduceVisionIssues[];
    occupancy_area: number | null;
  }[];
};

export type DeviceWakeUpParams = {
  image_id: number | null;
  boot_id: number | null;
  ota_version: string | null;
  config: Record<string, any> | null;
  schedule_id: string | null;
  status_payload: Record<string, any> | null;
  image_payload: Record<string, any> | null;
  skip_image: boolean | null;
  alarm_wake_up: boolean | null;
};

export interface CameraImageResponse {
  chainId: number;
  storeId: number;
  deviceName?: string | null;
  storeName: string;
  cameraId: number;
  image: CameraImage;
  pogs: Record<string, CameraPog>;
  templates: Record<string, CameraTemplate>;
}

export interface CameraImageTimelineResponse {
  images: CameraImage[];
  templates: Record<string, CameraTemplate>;
  pogs: Record<string, CameraPog>;
}

export interface CameraImage {
  imageId: number;
  imageUri: string;
  detectedAt: string; // ISO date string
  templateId: number;
  ilSectionIds: number[];
}

export interface CameraTemplate {
  templateId: number;
  bboxes: TemplateBBox[];
}

export interface CameraPog {
  ilSectionId: number;
  modCoverageId: number;
  pogImageUri: string;
  tagName: string;
  bboxes: PogBBox[];
}

// GET /v1/control/aisle-panorama/{imageId} (api_dl). Every x/y is in the shared aisle frame; a camera's transform maps
// its full-resolution image px into it: X = scale·x + tx, Y = scale·y + ty.
export type AislePanoramaFitQuality = 'ok' | 'weak' | 'failed';

export type AislePanoramaMissingReason =
  | 'no_image_in_window'
  | 'no_mod_polygons'
  | 'photo_unavailable';

export interface AislePanoramaMod {
  mod: number;
  modCoverageId: number;
  tagName: string;
  xMin: number | null;
  xMax: number | null;
}

export interface AislePanoramaFit {
  quality: AislePanoramaFitQuality;
  roundsUsed: number;
  spreadPx: number;
  inliers: number;
}

export interface AislePanoramaCamera {
  cameraId: number;
  /** Addresses the camera's page in Control; null when the camera has no device row. */
  deviceName: string | null;
  imageId: number;
  imageUri: string;
  capturedAt: string | null;
  perspectiveTemplateId: number | null;
  width: number;
  height: number;
  mods: AislePanoramaMod[];
  transform: { scale: number; tx: number; ty: number };
  seam: { xMin: number; xMax: number };
  fitToLeft: AislePanoramaFit | null;
}

export interface AislePanoramaMissingCamera {
  cameraId: number;
  tagNames: string[];
  reason: AislePanoramaMissingReason;
}

export interface AislePanoramaResponse {
  storeId: number;
  side: string;
  anchorImageId: number;
  mirrored: boolean;
  width: number;
  height: number;
  cameras: AislePanoramaCamera[];
  missingCameras: AislePanoramaMissingCamera[];
}

export interface CameraDetails {
  deviceName: string;
  cameraId: number;
  imageUri: string;
  imageCreatedAt: string; // ISO date string
  templateCreatedAt: string; // ISO date string
  pogCreatedAt: string; // ISO date string
  pogImageUri: string;
  mods: Mod[];
}

export interface Mod {
  modCoverageId: number;
  department: string;
  tagName: string;
  processingType: ProcessingType;
  isPrEnabled: boolean;
  isComplianceEnabled: boolean;
  complianceEligibilitySource: string;
  complianceEligibilityUpdatedAt: string;
  complianceEligibilityUpdatedByUserId: number;
  complianceEligibilityUpdatedByEmail: string;
}

export type ProcessingType =
  | 'structured'
  | 'flex-space'
  | 'off'
  | 'coffee-machine'
  | 'structured-produce'
  | 'structured-bakery'
  | 'checkout-queue'
  | 'entrance-exit'
  | 'checkout-indicator'
  | 'top-stock'
  | 'structured-with-pr-faceovers'
  | 'pr'
  | 'pr-without-faceovers';

export const PROCESSING_TYPES: ProcessingType[] = [
  // Described pipeline values first, in preferred order.
  'structured',
  'structured-with-pr-faceovers',
  'pr',
  'pr-without-faceovers',
  // Remaining values alphabetically.
  'checkout-indicator',
  'checkout-queue',
  'coffee-machine',
  'entrance-exit',
  'flex-space',
  'off',
  'structured-bakery',
  'structured-produce',
  'top-stock',
];

// Values routed through the PR pipeline — only allowed on mods with PC & PR enabled.
export const PR_PROCESSING_TYPES: ProcessingType[] = [
  'structured-with-pr-faceovers',
  'pr',
  'pr-without-faceovers',
];

// Human-readable pipeline description shown alongside the raw value in the picker.
export const PROCESSING_TYPE_DESCRIPTIONS: Partial<Record<ProcessingType, string>> = {
  structured: 'Core OOS',
  'structured-with-pr-faceovers': 'Core OOS + PR Faceovers',
  pr: 'PR OOS + PR Faceovers',
  'pr-without-faceovers': 'PR OOS',
};

export interface ModEligibility {
  modCoverageId: number;
  tagName: string | null;
  processingType: ProcessingType | null;
  department: string | null;
  cameraId: number | null;
  deviceName: string | null;
  hasImage: boolean;
  isExempt: boolean;
  isPaused: boolean;
  isTrackingExcluded: boolean;
  isPrEnabled: boolean;
  isComplianceEnabled: boolean;
  complianceEligibilitySource: string | null;
  complianceEligibilityUpdatedAt: string | null;
  complianceEligibilityUpdatedByUserId: number | null;
  complianceEligibilityUpdatedByEmail: string | null;
}

// Product Recognition and POG Compliance are enabled independently, but compliance consumes a PR
// inference, so 'compliance without PR' is not a legal state.
export type ModEligibilityMode = 'off' | 'pr' | 'pr-and-pc';

export const MOD_ELIGIBILITY_MODES: ModEligibilityMode[] = ['off', 'pr', 'pr-and-pc'];

export const MOD_ELIGIBILITY_MODE_LABELS: Record<ModEligibilityMode, string> = {
  off: 'Off',
  pr: 'PR only',
  'pr-and-pc': 'PR + PC',
};

export const modeOf = (mod: {
  isPrEnabled: boolean;
  isComplianceEnabled: boolean;
}): ModEligibilityMode => (mod.isComplianceEnabled ? 'pr-and-pc' : mod.isPrEnabled ? 'pr' : 'off');

export const modeToFlags = (
  mode: ModEligibilityMode
): { isPrEnabled: boolean; isComplianceEnabled: boolean } => ({
  isPrEnabled: mode !== 'off',
  isComplianceEnabled: mode === 'pr-and-pc',
});

export type ControlModEligibilityModeRequest = {
  modCoverageIds: number[];
  isPrEnabled: boolean;
  isComplianceEnabled: boolean;
};

export type ControlModProcessingTypeBatchRequest = {
  modCoverageIds: number[];
  processingType: ProcessingType;
};

export type ControlModEligibilityBatchResponse = {
  updatedCount: number;
};

export type PogResetTaskStatusFilter = 'open' | 'closed';
// 'automated' is a client-side view over open tasks: those the backend marks
// automationReady. It is not a server status.
// 'automated' and 'not_applicable' are views over open tasks, not server-side statuses.
export type PogResetTaskTab =
  | PogResetTaskStatusFilter
  | 'valid'
  | 'automated'
  | 'not_applicable'
  | 'future';

export type ControlPogResetMappingRun = {
  runId: string;
  status: 'running' | 'completed' | 'failed';
  createdAt: string;
  completedAt: string | null;
  error: string | null;
  summary: Record<string, number> | null;
};

export type ControlPogResetMappingStatus = {
  runId: string | null;
  computedAt: string | null;
  algorithmVersion: string | null;
  status: PogResetMatchStatus;
  issues: string[];
  matchedModCount: number;
  scoredModCount: number;
  scannedModCount: number;
  signedOffModCount: number;
  averageScore: number | null;
  averageCoverage: number | null;
  candidateTagNames: string[];
};

export type PogResetMatchStatus = 'matched' | 'partially_matched' | 'no_match' | 'not_processed';

export type ControlPogResetTaskSummary = {
  taskId: number;
  taskDate: string | null;
  pogStartDate: string | null;
  status: 'open' | 'closed';
  importedPogAssignmentId: number | null;
  pogName: string | null;
  pogNumber: string | null;
  modCount: number | null;
  reviewed: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  mapping: ControlPogResetMappingStatus | null;
  automationReady: boolean;
  autoResetAllowed: boolean;
  automationMods: ControlPogResetAutomationMod[];
};

export type ControlPogResetAutomationMod = {
  modNumber: number;
  tagName: string;
  source: string;
};

export type ControlPogResetTasksResponse = {
  storeId: number;
  tasks: ControlPogResetTaskSummary[];
  activeRun: ControlPogResetMappingRun | null;
};

export type ControlPogResetMappingClaim = {
  taskId: number;
  pogName: string | null;
  pogNumber: string | null;
  modNumber: number | null;
  score: number | null;
};

export type ControlPogResetModSuggestion = {
  taskId: number;
  pogName: string | null;
  pogNumber: string | null;
  modNumber: number | null;
  modCount: number | null;
  score: number | null;
  coverage: number | null;
  kind: 'proposed' | 'candidate';
  method: 'scan' | 'structural' | 'greedy' | null;
  scannedCount: number | null;
};

export type ControlPogResetModRow = {
  modCoverageId: number;
  taskId: number | null;
  tagName: string | null;
  cameraId: number | null;
  isTrackingExcluded: boolean;
  isPrEnabled: boolean;
  suggested: ControlPogResetModSuggestion | null;
  assignedPogName: string | null;
  assignedPogNumber: string | null;
  assignedModNumber: number | null;
  assignedModCount: number | null;
  assignedIsMirrored: boolean;
  assignedAt: string | null;
  isSignedOff: boolean;
  signedOffAt: string | null;
  signedOffBy: string | null;
};

export type ControlPogResetModsResponse = {
  storeId: number;
  mods: ControlPogResetModRow[];
};

export type ControlPogResetIdentification = {
  modCoverageId: number;
  tagName: string | null;
  cameraId: number | null;
  x: number;
  y: number;
  imageId: number | null;
  identifiedBy: string | null;
  identifiedAt: string | null;
};

export type ControlPogResetIdentificationsResponse = {
  storeId: number;
  identifications: ControlPogResetIdentification[];
};

export type ControlPogResetInstallMapPoint = {
  tagName: string;
  modNumber: number | null;
  x: number;
  y: number;
  status: 'not_installed' | 'installed' | 'signed_off' | 'assigned';
  source: 'camera' | 'plot';
  cameraId: number | null;
  modCoverageId: number | null;
  isAssigned: boolean;
  signedOffAt: string | null;
  signedOffBy: string | null;
  cameraHealth: 'ok' | 'missed_schedules' | 'offline';
  cameraHealthDescription: string | null;
  cameraHealthSince: string | null;
};

export type ControlPogResetInstallMapResponse = {
  storeId: number;
  mapUrl: string | null;
  points: ControlPogResetInstallMapPoint[];
  totalMods: number;
  installedMods: number;
  signedOffMods: number;
  assignedMods: number;
  offlineCameraMods: number;
  missedScheduleCameraMods: number;
};

export type ControlPogResetStatsScoreBucket = {
  minScore: number;
  mods: number;
  proposalCorrect: number;
};

export type ControlPogResetStatsMiss = {
  taskId: number;
  pogName: string | null;
  modNumber: number | null;
  appliedTag: string;
  proposedTag: string | null;
  proposedScore: number | null;
  candidateTags: string[];
};

export type ControlPogResetStatsResponse = {
  storeId: number;
  tasksTotal: number;
  modsTotal: number;
  taskAccuracy: { top1: number; top2: number; top3: number; manual: number };
  modAccuracy: {
    rank1: number;
    rank2: number;
    rank3: number;
    manual: number;
    manualPrDisabled: number;
    missing: number;
  };
  sequence: { consecutive: number; gapped: number; fragmented: number; single: number };
  proposal: {
    proposalCorrect: number;
    overridden: number;
    noProposal: number;
    noProposalPrDisabled: number;
  };
  scanVsPr: {
    bothPresent: number;
    agree: number;
    scanTotal: number;
    scanCorrect: number;
    prTotal: number;
    prCorrect: number;
    disagreements: number;
    disagreeScanCorrect: number;
    disagreePrCorrect: number;
    appliedFromScan: number;
  };
  scoreBuckets: ControlPogResetStatsScoreBucket[];
  misses: ControlPogResetStatsMiss[];
};

export type ControlPogResetMappingCandidate = {
  rank: number;
  modCoverageId: number;
  tagName: string | null;
  cameraId: number | null;
  deviceName: string | null;
  score: number;
  precision: number | null;
  coverage: number | null;
  matchedCount: number;
  realogramItemCount: number | null;
  inferenceId: string | null;
  imageId: number | null;
  imageUri: string | null;
  inferenceCreatedAt: string | null;
  prMod: number | null;
  obstructionRatio: number | null;
  isTrackingExcluded: boolean;
  assignedPogName: string | null;
  assignedPogNumber: string | null;
  claimedBy: ControlPogResetMappingClaim | null;
  isSignedOff: boolean;
};

/** How the backend settled an ambiguous install scan (or filled/queried a mod). */
export type ScanResolution =
  | 'sequence'
  | 'end_cap'
  | 'assigned_elsewhere'
  | 'gap_fill'
  | 'unique_fit';

export type ControlPogResetMappingProposed = {
  modCoverageId: number;
  tagName: string | null;
  score: number;
  method: 'scan' | 'structural' | 'greedy';
  scanAmbiguous: boolean;
  scanResolved: ScanResolution | null;
  isTrackingExcluded: boolean;
  assignedPogName: string | null;
  assignedPogNumber: string | null;
  claimedBy: ControlPogResetMappingClaim | null;
  isSignedOff: boolean;
};

export type ControlPogResetPlanogramProduct = {
  upc: string | null;
  productName: string | null;
  itemNumber: string | null;
  shelfNumber: number | null;
  positionNumber: number | null;
  horizontalFacings: number | null;
  verticalFacings: number | null;
  depthFacings: number | null;
  price: number | null;
  casePack: number | null;
  inventoryLevel: number | null;
  imageUri: string | null;
  bbox: [number, number, number, number] | null;
  embeddingCropCount: number | null;
};

export type ControlPogResetMappingMod = {
  modNumber: number | null;
  productCount: number;
  planogramItemCount: number;
  planogramImageUri: string | null;
  products: ControlPogResetPlanogramProduct[];
  proposed: ControlPogResetMappingProposed | null;
  scanCandidates: ControlPogResetScanCandidate[];
  flags: string[];
  candidates: ControlPogResetMappingCandidate[];
};

export type ControlPogResetScanCandidate = {
  modCoverageId: number;
  tagName: string | null;
  score: number;
  cameraId: number | null;
  deviceName: string | null;
  inferenceId: string | null;
  imageId: number | null;
  imageUri: string | null;
  inferenceCreatedAt: string | null;
  prMod: number | null;
  assignedPogName: string | null;
  assignedPogNumber: string | null;
  claimedBy: ControlPogResetMappingClaim | null;
  isSignedOff: boolean;
  scanResolved: ScanResolution | null;
  scanSuspect: boolean;
  // 1/null = the tag's best match at install; >1 = also-matched planogram saved alongside it.
  matchRank: number | null;
  // The tag's install scan had best-rank matches in more than one POG (a tie at install).
  tiedBest: boolean;
  // How many OTHER planogram assignments this tag's install scan also matched (any rank).
  otherPogMatches: number;
  scannedProducts: ControlPogResetScannedProduct[];
  confirmedAt: string | null;
};

export type ControlPogResetScannedProduct = {
  barcode: string;
  itemNumber: string | null;
  productName: string | null;
  productId: number | null;
  inPlanogram: boolean;
  scannedAt: string | null;
};

export type ControlPogResetTagOccupancy = {
  tagName: string;
  modCoverageId: number | null;
  assignedPogName: string | null;
  assignedPogNumber: string | null;
  exists: boolean;
};

export type ControlPogResetTagOccupancyResponse = {
  storeId: number;
  tags: ControlPogResetTagOccupancy[];
};

export type ControlPogResetTagScan = {
  importedPogAssignmentId: number;
  modNumber: number | null;
  score: number;
  confirmedAt: string | null;
  pogName: string | null;
  pogNumber: string | null;
  isOwnTask: boolean;
};

export type ControlPogResetTagEvidence = {
  tagName: string;
  exists: boolean;
  modCoverageId: number | null;
  cameraId: number | null;
  deviceName: string | null;
  imageId: number | null;
  imageUri: string | null;
  imageDetectedAt: string | null;
  isSignedOff: boolean;
  assignedPogName: string | null;
  assignedPogNumber: string | null;
  scans: ControlPogResetTagScan[];
};

export type ControlPogResetTagEvidenceResponse = {
  storeId: number;
  tags: ControlPogResetTagEvidence[];
};

export type ControlPogResetImportedPog = {
  pogName: string | null;
  pogNumber: string | null;
  assortmentGroup: string | null;
  startDate: string | null;
  departmentName: string | null;
};

export type ControlPogResetMappingConfig = {
  minCandidateSimilarity: number;
  minPairScore: number;
  includeCartonPolygons: boolean | null;
  skipPr: boolean | null;
  tagPrefix: string | null;
};

export type ControlPogResetConfirmedMappingMod = {
  modNumber: number;
  tagName: string | null;
  source: string;
};

export type ControlPogResetConfirmedMapping = {
  isMirrored: boolean;
  confirmedBy: string | null;
  confirmedAt: string | null;
  mods: ControlPogResetConfirmedMappingMod[];
};

export type ControlPogResetTaskDetailResponse = {
  automationReady: boolean;
  taskId: number;
  taskDate: string | null;
  storeName: string | null;
  storeCode: string | null;
  storeLegibleName: string | null;
  autoResetAllowed: boolean;
  reviewed: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  importedPog: ControlPogResetImportedPog | null;
  runId: string | null;
  computedAt: string | null;
  algorithmVersion: string | null;
  config: ControlPogResetMappingConfig | null;
  status: PogResetMatchStatus;
  issues: string[];
  confirmedMapping: ControlPogResetConfirmedMapping | null;
  mods: ControlPogResetMappingMod[];
};

export type ProductRecognitionLayerType =
  | 'productBoxes'
  | 'productPolygons'
  | 'cartonBoxes'
  | 'cartonPolygons'
  | 'priceTagBoxes'
  | 'modPolygons'
  | 'shelfBoxes'
  | 'templateBoxes'
  | 'templateBoxesUp'
  | 'visualObstructions'
  | 'merchandise'
  | 'hookArea'
  | 'fridgeDoors'
  | 'topStock'
  | 'shelfEdges'
  | 'pallet';

export enum ShapeType {
  PRODUCT = 'product',
  CARTON = 'carton',
  TEMPLATE_BOX = 'template_box',
  TEMPLATE_BOX_UP = 'template_box_up',
  HOOK_AREA = 'hook_area',
  FRIDGE_DOOR = 'fridge_door',
  MERCHANDISE = 'merchandise',
  MISPLACED_ITEM = 'misplaced_item',
  CART_OR_PRODUCT = 'cart_or_product',
  BLACK_MASK = 'black_mask',
  PRICE_TAG = 'price_tag',
  MOD = 'mod',
  SHELF_AREA = 'shelf_area',
  SHELF_EDGE = 'shelf_edge',
  TOP_STOCK = 'top_stock',
  FIXTURES = 'fixtures',
  LENSE_BLOCKING = 'lense_blocking',
}

export interface OCRResponse {
  text?: string;
  confidence?: OcrConfidence;
}

export interface ProductRecognitionShape {
  uuid?: string;
  type?: ShapeType;
  confidence?: number;
  ocr?: OCRResponse;
  column?: number;
  mod?: number;
  shelf?: number;
  vizSimCandidates?: VizSimCandidate[];
  evaluationGtLabel?:
    | 'catalog_item'
    | 'non_product'
    | 'unresolved_product'
    | 'scan_unresolved'
    | 'unscanned_unlabelled';
  /** Human-verified identity; present only when evaluationGtLabel === 'catalog_item'. */
  evaluationGtNormalizedBarcode?: string;
}

export type VizSimReferenceCrop = {
  embeddingCropId: string;
  similarity: number;
};

export type VizSimCandidate = {
  normalizedBarcode: string;
  similarity: number | null;
  isConfident: boolean;
  productName?: string;
  itemNumber?: string;
  productImageUri?: string;
  productId?: number;
  isExpectedPog?: boolean;
  referenceCrops?: VizSimReferenceCrop[];
  allNormalizedBarcodes?: string[];
  isActive: boolean;
};

export interface ProductRecognitionBBox extends ProductRecognitionShape {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  shape: [number, number, number, number]; // [x, y, width, height]
}

export enum DecisionChangeCategory {
  LOW_CONFIDENCE_FILTERING = 'low_confidence_filtering',
  PLANOGRAM_FILTERING = 'planogram_filtering',
  STACKING = 'stacking',
  NEIGHBOUR_FILTERING = 'neighbour_filtering',
  POSITION_CORRECTION = 'position_correction',
  BLEEDOVER_FORGIVENESS = 'bleedover_forgiveness',
  OUT_FORGIVENESS = 'out_forgiveness',
  OUTPUT_NORMALIZATION = 'output_normalization',
}

export interface DecisionAdjustment {
  category: DecisionChangeCategory;
  reason: string;
  changes: Record<string, { from: unknown; to: unknown }>;
}

export interface PlanogramComplianceBox {
  imageId: number;
  rogItemId: number | null;
  rogItemUpc: string | null;
  prTop1Upc?: string | null;
  decisionAdjustment?: DecisionAdjustment | null;
  x1: number | null;
  y1: number | null;
  x2: number | null;
  y2: number | null;
  templateCoordinates: [number, number, number, number] | null; // [x1, y1, x2, y2]
  shape: [number, number, number, number] | null; // [x, y, width, height] - bounding box of polygon
  rogItemStatus: RogItemStatus;
  // DLI-1532: id of the swap group this box belongs to (null/absent for non-swaps). All legs of
  // one physical swap share the id; drives the swap-pair highlighting/mapping in the viewer.
  swapGroupId?: number | null;
  // LIDL sequence compliance POC: 1-based index of the box's collapsed entry in the merged-shelf
  // realogram sequence; null/absent for outs, POG-only boxes and the legacy pipeline.
  sequenceIndex?: number | null;
  // Merged-shelf number both sequence indices refer to, and the POG item's own 1-based P entry index.
  sequenceShelf?: number | null; // merged shelf of the realogram box (pairs with sequenceIndex)
  pogSequenceShelf?: number | null; // merged shelf of the POG item (pairs with pogSequenceIndex)
  pogSequenceIndex?: number | null;
  rogPositionVector: {
    mod: number;
    shelf: number;
    position: number;
    tagName: string;
    modCoverageId: number;
  };
  pogItemId: number;
  pogItemUpc: string;
  osaStatus: OosDetectionState;
  complianceStatus: ComplianceStatus;
  pogPositionVector: {
    mod: number;
    shelf: number;
    position: number;
    tagName: string;
    modCoverageId: number;
  };
  productName: string;
  itemNumber: string;
  productImageUri: string;
}

export interface PolygonPoint {
  x: number;
  y: number;
}

export interface ProductRecognitionPolygon extends ProductRecognitionShape {
  points: PolygonPoint[];
  shape: [number, number, number, number]; // [x, y, width, height] - bounding box of polygon
}

export interface ProductRecognitionMask extends ProductRecognitionShape {
  pngData: {
    b64: string;
  };
  shape: [number, number, number, number]; // [x, y, width, height] - added at runtime by api.ts
}

export interface InferenceMetadata {
  ssm_plus_version?: string;
  visual_embedding_model_version?: string;
  oos_model_version?: string;
  steps_run?: string[];
  [key: string]: unknown;
}

export interface ProductRecognitionResponse {
  imageId?: number;
  inferenceId: string;
  inferenceMetadata?: InferenceMetadata;
  merchandise: ProductRecognitionMask[];
  productBoxes: ProductRecognitionBBox[];
  productPolygons: ProductRecognitionPolygon[];
  cartonBoxes: ProductRecognitionBBox[];
  cartonPolygons: ProductRecognitionPolygon[];
  priceTagBoxes: ProductRecognitionBBox[];
  modPolygons: ProductRecognitionPolygon[];
  shelfBoxes: ProductRecognitionBBox[];
  templateBoxes: ProductRecognitionBBox[];
  templateBoxesUp: ProductRecognitionBBox[];
  visualObstructions: ProductRecognitionMask[];
  hookArea: ProductRecognitionMask[];
  fridgeDoors: ProductRecognitionMask[];
  topStock: ProductRecognitionMask[];
  shelfEdges: ProductRecognitionMask[];
  pallet: ProductRecognitionMask[];
  corrections?: ProductRecognitionCorrection[];
  updatedAt?: string;
  isV2?: boolean;
}

export interface ProductRecognitionCorrection {
  shapeList: string;
  index: number;
  upc: string;
  itemNumber?: string;
}

export interface TagComplianceOrphanPriceTag {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  ocr_text: string | null;
  ocr_confidence: string | null;
  containment: number | null;
}

export interface TagComplianceShelf {
  modCoverageId: number;
  tagName: string;
  mod: number;
  shelf: number;
  pogPositions: number;
  distinctProducts: number;
  priceTags: number;
  subtaskType: string | null;
  count: number | null;
  skipReason: string | null;
  priceCheck: boolean;
  clipstripCredit: number | null;
  midshelfCredit: number | null;
  clipstripPresent: boolean;
  midshelfCoveredGaps: number;
  orphanPriceTags: TagComplianceOrphanPriceTag[];
  candidateTagAdoptions: unknown[];
  templateBoxCoords: { x1: number; y1: number; x2: number; y2: number }[];
}

export interface TagCompliancePreview {
  config: Record<string, unknown>;
  totals: {
    tasks: number;
    subtasks: number;
    missingTags: number;
    extraTags: number;
    priceCheckShelves: number;
    skipped: Record<string, number>;
  };
  shelves: TagComplianceShelf[];
}

export interface PlanogramComplianceResponse {
  swapGroups?: Record<string, PlanogramComplianceBox[]>;
  swapImages?: Record<string, string>;
  imageId: number;
  tagCompliancePreview?: TagCompliancePreview | null;
  boxes: PlanogramComplianceBox[];
  osaCompliance: Record<string, number>;
  pogCompliance: Record<string, number | null>;
  // Optional per-shelf clip-strip repair decisions, keyed by "{mod}-{shelf}".
  // "L"/"R" = synthetic price tag added on that side; "B" = considered, no tag.
  // Absent when the clip-strip repair did not run.
  clipStripDecisions?: Record<string, 'B' | 'L' | 'R'>;
  oversizedPtDecisions?: Record<string, string>;
  // Cross-mod shelf stitching report (experimental): detected shelf boxes with raw and stitched numbers.
  shelfStitching?: ShelfStitchEntry[] | null;
}

export interface ShelfStitchEntry {
  imageId: number;
  mod: number;
  modCoverageId?: number | null;
  rawShelf: number;
  shelf: number;
  x1?: number | null;
  y1?: number | null;
  x2?: number | null;
  y2?: number | null;
  matchError?: number | null;
  // False when no partner within the stitching threshold (fresh, unique shelf number).
  matched?: boolean;
}

export interface PlanogramComplianceConfigItem {
  key: string;
  type: 'number' | 'boolean' | 'enum' | 'multi_enum';
  defaultValue: number | boolean | string | string[];
  value: number | boolean | string | string[];
  label: string;
  options: string[] | null;
}

export interface PlanogramCompliancePayload extends ProductRecognitionResponse {
  config?: Record<string, unknown>;
}

// Embedding types
export type ProductEmbeddingCropSource = 'at' | 'control' | 'scan' | 'other';

export type ProductEmbeddingCropSortField =
  | 'created_at'
  | 'updated_at'
  | 'normalized_barcode'
  | 'created_by'
  | 'store_id'
  | '_id';

export type HttpSortingOrderEnum = 'asc' | 'desc';

export type ImageResolution = '5mp' | '8mp';

export type Orientation = 'front' | 'not-front';

export type ClassificationType = 'product' | 'carton' | 'template_box';

export interface Coords {
  bbox: [number, number, number, number];
}

export interface CropEmbeddingSearchResponse {
  productEmbeddingCropId?: string;
  storeId: number;
  chainId: number;
  barcode: string;
  normalizedBarcode?: string;
  productName?: string;
  manualReview: boolean;
  source: ProductEmbeddingCropSource;
  imageId: number;
  imageResolution: ImageResolution;
  coords: Coords;
  vizUrl: string;
  packageVariant?: string;
  orientation: Orientation;
  classificationType: ClassificationType;
  transformationMatrix?: string;
  isActive: boolean;
  isPending: boolean;
  createdBy: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  productImage?: string;
  itemNumber?: number;
  modTags: string[];
  designId?: string | null;
  designName?: string | null;
}

export interface UpdatedEmbeddingsResponse {
  embeddingCropId: string;
  embeddingsCount: number;
}

export interface EmbeddingCountByBarcode {
  barcode: string;
  embeddingsCount: number;
}

export enum ProductImageSource {
  ShelfCam = 'shelf-cam',
  AutoCheckout = 'auto-checkout',
  Online = 'online',
  UserUpload = 'user-upload',
  Embedding = 'embedding',
}

export interface ProductImage {
  productImageId?: number;
  imageUri: string;
  position: number;
  chainId: number;
  source: ProductImageSource;
  origin?: string;
}

export interface ProductImageCreateResponse {
  product_image_id: number;
  upc: string;
  image_uri: string;
  position: number;
}

export interface ProductImageReorderItem {
  product_image_id: number;
  position: number;
}

export interface CandidatesSearchParams {
  chainId: number;
  upc: string;
  storeId?: number;
  sortField?: ProductEmbeddingCropSortField;
  sortOrder?: HttpSortingOrderEnum;
  page?: number;
  limit?: number;
}

export interface EmbeddingSearchParams {
  chainId: number;
  storeId?: number;
  normalizedBarcodes?: string;
  isActive?: boolean;
  source?: ProductEmbeddingCropSource;
  extendedSearch?: boolean;
  includeItemNumber?: boolean;
  itemNumber?: string;
  barcode?: string;
  createdBy?: string;
  sortField?: ProductEmbeddingCropSortField;
  sortOrder?: HttpSortingOrderEnum;
  page?: number;
  limit?: number;
}

// Product Recognition Crop Scan Types
export type ProductRecognitionCropScanResponse = {
  product_crop_scan_id: number;
  parent_product_crop_scan_id?: number;
  order: number;
  classification_type: ClassificationType;
  orientation_class: Orientation;
  valuable_to_classify: boolean;
  bbox: Coords['bbox'];
  points: object[];
  barcode?: string;
  normalized_barcode?: string;
  product_id?: number;
  product_name?: string;
  product_image_uri?: string;
};

export type ProductRecognitionImageCropScansResponse = {
  image_url: string;
  crop_scans: ProductRecognitionCropScanResponse[];
};

export type ProductRecognitionCropScanUpdate = {
  product_crop_scan_id: number;
  barcode?: never; // For now there is no way to tell which variant we are updating
  normalized_barcode: string;
  product_id: number | null;
  orientation?: 'front' | 'not-front';
  design_id?: string;
};

export type ProductVariantResponse = {
  upc: string;
  name: string | null;
  casePack: number | null;
  isCaseUpc: boolean;
  isActive: boolean;
  image: string | null;
  frontImage: string | null;
  backImage: string | null;
  itemNumber?: string;
};

export type ProductBarcodeResponse = {
  barcode: string;
  normalized_barcode: string;
  normalized_upc?: string;
  product_id?: number;
  product_name?: string;
  product_image_uri?: string;
  item_number?: string;
  inventory_level?: number;
};

export type StoreProductSearchResult = {
  case_upc?: string[] | null;
  imageUrl?: string | null;
  isActive?: boolean | null;
  itemNumber?: string | null;
  msiNumber?: string | null;
  name?: string | null;
  productId?: number | null;
  upc: string;
};

export type StoreProductSearchResponse = {
  products: StoreProductSearchResult[];
};

export type EmbeddingCandidateResponse = {
  bboxCoords: number[];
  bboxId: number;
  column: number;
  templateId: number;
  templateCreatedAt: string;
  imageId: number;
  imageUri: string;
  imageCreatedAt: string;
  imageResolution: ImageResolution;
  ilSectionId: number;
  ilItemId: number;
  pogImageUri: string;
  cameraId: number;
  deviceName: string;
  storeId: number;
  storeName: string;
  legibleName: string;
  tagName: string;
  pogBoxCoords: number[];
  pogImageId: number;
  shelf: number;
};

export type EmbeddingCandidate = EmbeddingCandidateResponse & {
  cropImageUri: string;
  pogCropImageUri: string;
};

export type StatusResponse = {
  status: string;
  message?: string;
};

// Product Recognition API Types
export type V1GetProductRecognitionCropScansApiResponse =
  /** status 200 Successful Response */ ProductRecognitionImageCropScansResponse;
export type V1GetProductRecognitionCropScansApiArg = {
  /** Image ID to get crop scans for */
  imageId: number;
};

export type V1CreateProductRecognitionCropScansApiResponse =
  /** status 200 Successful Response */ ProductRecognitionImageCropScansResponse;
export type V1CreateProductRecognitionCropScansApiArg = {
  /** Image ID to create crop scans for */
  imageId: number;
};

export type V1UpdateProductRecognitionCropScansApiResponse =
  /** status 200 Successful Response */ StatusResponse;
export type V1UpdateProductRecognitionCropScansApiArg = {
  imageId: number;
  payload: ProductRecognitionCropScanUpdate[];
};

export type V1FinishProductRecognitionCropScanSessionApiResponse =
  /** status 200 Successful Response */ StatusResponse;
export type V1FinishProductRecognitionCropScanSessionApiArg = {
  imageId: number;
};

export type V1GetStoreProductByBarcodeApiResponse =
  /** status 200 Successful Response */ ProductBarcodeResponse;
export type V1GetStoreProductByBarcodeApiArg = {
  /** Store name to lookup product in */
  storeName: string;
  /** Barcode to lookup */
  barcode: string;
};

// Task types
export interface Task {
  actionTaskId: number;
  aisleId: string | null;
  aisleName: string | null;
  cameraId: number;
  chainId: number;
  chainName: string;
  customTaskId: number;
  deviceName: string;
  imageCaptureTime: string;
  imageId: number;
  imageUri: string;
  isAudited: boolean;
  isActive: boolean;
  isReviewed: boolean;
  isWorked: boolean;
  itemAisle: string;
  itemAisleSide: string;
  itemDepartment: string;
  itemMod: string;
  itemShelfTagName: string;
  modCoverageId: number;
  numberOfSubtasks: number;
  osaComplianceScore: number;
  pogComplianceScore: number | null;
  productRecognitionInferenceId: number;
  lockedByUserId: string | null;
  lockedByUserName: string | null;
  reviewAssignedAt: string | null;
  storeId: number;
  storeName: string;
  tagName: string;
  taskCreatedAt: string;
  taskDate: string;
  taskId: number;
}

export interface TaskExtended extends Task {
  atUrl: string;
}

export type ReviewStatus = 'reviewed' | 'non_reviewed';
export type TaskSortField =
  | 'pog_compliance_score'
  | 'osa_compliance_score'
  | 'tag_name'
  | 'task_created_at'
  | 'image_capture_time';
export type TaskSortOrder = 'asc' | 'desc';

export interface TasksSearchParams {
  chainId: number;
  imageId?: string;
  storeId?: number;
  tagNamePrefix?: string;
  reviewStatus?: ReviewStatus;
  taskDate?: string; // YYYY-MM-DD format
  isActive?: boolean;
  isAudited?: boolean;
  page?: number;
  limit?: number;
  sortField?: TaskSortField;
  sortOrder?: TaskSortOrder;
}

export interface TasksListStorage {
  chainId: number;
  tasks: Task[];
}

export interface TaskPogResponse {
  pogs: Record<string, CameraPog>;
}

export enum EmbeddingSelectionMode {
  ACTIVATING = 'activating',
  DEACTIVATING = 'deactivating',
}

export interface UpcCorrection {
  shape_list: string;
  index: number;
  upc: string;
  item_number?: string;
}

export enum UpcCorrectionCanceledReason {
  OBSTRUCTED = 'obstruction',
  BLURRY = 'blurry',
  MISSING_PRODUCT_BOX = 'missing_product_box',
}

export interface UpcCorrectionPayload {
  corrections: UpcCorrection[];
  confidence: number;
  canceled?: UpcCorrectionCanceledReason;
}

export type TaskLockStatusResponse =
  | {
      lockedByUserId: string;
      lockedByUserName: string;
      reviewAssignedAt: string;
      ttl: number; // Time in seconds until the lock expires
    }
  | {
      lockedByUserId: null;
      lockedByUserName: null;
      reviewAssignedAt: null;
      ttl: null;
    };

export type BarcodeStatusEnum = 'unconfirmed' | 'pending' | 'confirmed';

export interface ForgeDesignNote {
  id?: string | null;
  text: string;
  createdBy?: string | null;
  createdAt?: string | null;
}

export interface ForgeDesignGroupSummary {
  designId: string;
  normalizedBarcode?: string | null;
  previewVizUrl?: string | null;
  forgeUrl?: string | null;
  barcodeStatus?: BarcodeStatusEnum | string | null;
  cropCount?: number | null;
  noteCount?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface ForgeDesignGroupReviewSummary {
  groupId: string;
  isReviewed: boolean;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
}

export interface DesignResponse {
  _id: string;
  chainId: number;
  representativeCropId?: string;
  representativeVizUrl?: string;
  barcodeStatus: BarcodeStatusEnum;
  frontImageUrl?: string;
  backImageUrl?: string;
  barcodeImageUrl?: string;
  itemNumber?: string;
  normalizedBarcode?: string;
  notes?: ForgeDesignNote[];
  isReviewed?: boolean;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  fullGroupReview?: ForgeDesignGroupReviewSummary | null;
  fullGroupDesigns?: ForgeDesignGroupSummary[];
  partialGroupReview?: ForgeDesignGroupReviewSummary | null;
  partialGroupDesigns?: ForgeDesignGroupSummary[];
  nonFrontGroupReview?: ForgeDesignGroupReviewSummary | null;
  nonFrontGroupDesigns?: ForgeDesignGroupSummary[];
  productName?: string;
  cropCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface DesignSearchResponse {
  results: DesignResponse[];
  total: number;
  page: number;
  limit: number;
}

export interface DesignSearchParams {
  chainId: number;
  storeName?: string;
  itemNumbers?: string[];
  normalizedBarcodes?: string[];
  barcode?: string;
  page?: number;
  limit?: number;
}

export interface CreateEmptyDesignRequest {
  storeName: string;
  barcode: string;
  representativeCropId: string;
}

export interface CreateEmbeddingsCropMapping {
  product_crop_scan_id: number;
  product_embedding_crop_id: string;
}

export interface AssignDesignCropsRequest {
  cropIds: string[];
  designId: string;
}

export interface AssignDesignCropsResponse {
  designId: string;
  cropsModified: number;
  representativeCropId?: string;
}

export interface CreateDesignResponse {
  _id: string;
  cropsAssigned: number;
}

export interface PogCompTemplateBox {
  modNumber: number;
  shelfNumber: number;
  positionNumber: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  strategy: string;
  isSynthetic: boolean;
  upc?: string | null;
  productId?: number | null;
  sourceType?: string | null;
  modCoverageId: number;
}

export interface MatchLabelingIssue {
  type: string;
  details: string | null;
  modCoverageId: number | null;
  userId: number;
  createdAt: string; // ISO date string
}

export interface CameraIssuesResponse {
  cameraId: number;
  matchTaskId: number | null;
  issues: MatchLabelingIssue[];
  // Whether compliance tasks really are being withheld for this camera right now. Issues are
  // reported regardless, so the banner can be checked against real cameras before the backend
  // flag is enabled for the store.
  tasksSuppressed: boolean;
  // The camera's open follow-up work, so Control can warn before someone reports it again.
  fieldTasks: CameraFieldTask[];
  actionTasks: CameraActionTask[];
}

export interface PogRule {
  pogRuleId: number;
  chainId: number;
  departmentNumber: string | null;
  departmentName: string | null;
  categoryNumber: string | null;
  categoryName: string | null;
  createPogResetTasks: boolean;
  allowAutoPogReset: boolean;
  measurePogComplianceWithoutTask: boolean;
  createPogComplianceTasks: boolean;
  createPsiTasks: boolean;
  updatedAt: string | null;
}

export interface PogRulesResponse {
  rules: PogRule[];
  count: number;
}

export interface PogRuleCreateRequest {
  departmentNumber: string | null;
  departmentName: string | null;
  categoryNumber: string | null;
  categoryName: string | null;
  createPogResetTasks?: boolean;
  allowAutoPogReset?: boolean;
  measurePogComplianceWithoutTask?: boolean;
  createPogComplianceTasks?: boolean;
  createPsiTasks?: boolean;
}

export interface PogRuleFlagUpdate {
  pogRuleId: number;
  createPogResetTasks?: boolean;
  allowAutoPogReset?: boolean;
  measurePogComplianceWithoutTask?: boolean;
  createPogComplianceTasks?: boolean;
  createPsiTasks?: boolean;
}

export interface PogRuleBatchUpdateRequest {
  updates: PogRuleFlagUpdate[];
}

export interface PogRuleBatchUpdateResponse {
  updatedCount: number;
}

export interface PogRuleScope {
  departmentNumber: string | null;
  departmentName: string | null;
  categoryNumber: string | null;
  categoryName: string | null;
}

export interface PogRuleUncoveredScope extends PogRuleScope {
  pogs: number;
  liveAssignments: number;
  liveStores: number;
  earliestStart: string | null;
  latestEnd: string | null;
}

export interface PogRuleUncoveredCountResponse {
  count: number;
}

export interface PogRuleUncoveredResponse {
  scopes: PogRuleUncoveredScope[];
  count: number;
}

export interface PogRuleFlagChange {
  flag: string;
  before: boolean;
  after: boolean;
}

export interface PogRuleImportUpdate {
  pogRuleId: number;
  scope: PogRuleScope;
  changes: PogRuleFlagChange[];
}

/** What an import would write. Returned by a dry run before anything is applied. */
export interface PogRuleImportPlan {
  toInsert: PogRuleScope[];
  toUpdate: PogRuleImportUpdate[];
  stale: PogRuleScope[];
  insertCount: number;
  updateCount: number;
  unchangedCount: number;
  staleCount: number;
}

export interface PogRuleImportResponse {
  applied: boolean;
  plan: PogRuleImportPlan;
}

export type PogRuleFlagKey =
  | 'createPogResetTasks'
  | 'allowAutoPogReset'
  | 'measurePogComplianceWithoutTask'
  | 'createPogComplianceTasks'
  | 'createPsiTasks';

export const POG_RULE_FLAGS: {
  key: PogRuleFlagKey;
  label: string;
  shortLabel: string;
  enforced: boolean;
}[] = [
  { key: 'createPogResetTasks', label: 'POG Reset', shortLabel: 'Reset tasks', enforced: true },
  {
    key: 'createPogComplianceTasks',
    label: 'Compliance Tasks',
    shortLabel: 'PC Tasks',
    enforced: true,
  },
  {
    key: 'measurePogComplianceWithoutTask',
    label: 'Compliance Measurement',
    shortLabel: 'PC Measurement',
    enforced: true,
  },
  { key: 'createPsiTasks', label: 'PSI Tasks', shortLabel: 'PSI', enforced: false },
  { key: 'allowAutoPogReset', label: 'Auto POG Reset', shortLabel: 'Auto Reset', enforced: true },
];

/**
 * Shelf-alignment diagnostic states, in the order the pipeline decides them.
 *
 * Only `flagged` is raised to the store; the other two are mods the diagnostic
 * deliberately withheld, kept in the response so a wrong exclusion is visible
 * here rather than silent.
 */
export type ShelfAlignmentState = 'flagged' | 'hook_excluded' | 'empty_extra_shelf';

export type ShelfAlignmentReason = 'extra_unreconcilable' | 'missing_shelf';

export interface ShelfAlignmentMod {
  modCoverageId: number | null;
  tagName: string | null;
  state: ShelfAlignmentState;
  reason: ShelfAlignmentReason | null;
  /** What the camera saw. */
  detectedShelves: number | null;
  /** What the planogram says. */
  pogShelves: number | null;
  /** 0..1 share of the mod covered by the hook mask; `null` means NOT MEASURED,
   * which the pipeline keeps distinct from a measured 0. */
  hookCoverage: number | null;
  /** One `[x1, y1, x2, y2]` per detected shelf, top to bottom. Empty means the
   * row predates the geometry being recorded — not "zero shelves". */
  shelfBoxes: number[][];
  /** Products per shelf, index-aligned with `shelfBoxes`. */
  productsByShelf: number[];
  /** Only on `empty_extra_shelf`: the phantom shelf that caused the exclusion. */
  extraShelfBbox?: number[] | null;
  extraShelfProducts?: number | null;
}

export interface ShelfAlignmentResponse {
  imageId: number;
  /** One row per mod the diagnostic had something to say about. Mods that
   * matched their planogram are absent, so an empty list means all good. */
  mods: ShelfAlignmentMod[];
}
